import { t } from "i18next";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Sidebar } from "primereact/sidebar";
import { useContext, useState } from "react";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import { useApis } from "../api";
import { NotificationContext } from "../context/NotificationContext";
import { useAuth } from "../hooks/useAuth";
import { timezoneOptions } from "../hooks/useDate";
import { LayoutContext } from "./context/layoutcontext";

const AppProfileSidebar = () => {
  const { addNotification } = useContext(NotificationContext);
  const { layoutState, setLayoutState } = useContext(LayoutContext);
  const { getAuthUser, deleteAuthData, saveAuthUser } = useAuth();
  const { sendPasswordResetEmail, logout, updateUser } = useApis();

  const user = getAuthUser();
  const navigate = useNavigate();

  const [isChangingTimezone, setIsChangingTimezone] = useState(false);
  const [timeZone, setTimeZone] = useState<string>(user.timeZone);

  const onProfileSidebarHide = () => {
    setLayoutState((prevState) => ({
      ...prevState,
      profileSidebarVisible: false,
    }));
  };

  const mUpdateUser = useMutation(updateUser, {
    onSuccess: (res) => {
      saveAuthUser(res.user);
      addNotification({
        title: t("common.updateSuccess"),
        type: "success",
      });
    },
    onError: () => {
      addNotification({
        title: t("common.genericError"),
        type: "error",
      });
    },
  });

  const mForgotPassword = useMutation(sendPasswordResetEmail, {
    onSuccess: () => {},
    onError: () => {},
  });

  const mLogout = useMutation(logout, {
    onSuccess: () => {
      deleteAuthData();
      navigate("/login");
    },
  });

  return (
    <Sidebar
      visible={layoutState.profileSidebarVisible}
      onHide={onProfileSidebarHide}
      position="right"
      className="layout-profile-sidebar w-full sm:w-25rem"
    >
      <div className="flex flex-column mx-auto md:mx-0">
        <span className="mb-2 font-semibold">
          {t("layout.appProfileSidebar.title")}
        </span>
        <span className="text-color-secondary font-medium mb-5">
          {user?.name}
        </span>

        <ul className="list-none m-0 p-0">
          <li
            onClick={() => {
              addNotification({
                title: t(
                  "layout.appProfileSidebar.actions.changePassword.success",
                ),
                type: "success",
              });
              mForgotPassword.mutate(user?.email);
            }}
          >
            <div className="cursor-pointer flex surface-border mb-3 p-3 align-items-center border-1 surface-border border-round hover:surface-hover transition-colors transition-duration-150">
              <span>
                <i className="pi pi-cog text-xl text-primary"></i>
              </span>
              <div className="ml-3">
                <span className="mb-2 font-semibold">
                  {t("layout.appProfileSidebar.actions.changePassword.title")}
                </span>
                <p className="text-color-secondary m-0">
                  {t(
                    "layout.appProfileSidebar.actions.changePassword.description",
                  )}
                </p>
              </div>
            </div>
          </li>
          <li
            onClick={() => {
              setIsChangingTimezone(true);
            }}
          >
            <div className="cursor-pointer flex surface-border mb-3 p-3 align-items-center border-1 surface-border border-round hover:surface-hover transition-colors transition-duration-150">
              <span>
                <i className="pi pi-calendar text-xl text-primary"></i>
              </span>
              <div className="ml-3">
                <span className="mb-2 font-semibold">
                  {t("layout.appProfileSidebar.actions.changeTimezone.title")}
                </span>
                <p className="text-color-secondary m-0">
                  {t(
                    "layout.appProfileSidebar.actions.changeTimezone.description",
                  )}
                </p>
              </div>
            </div>
          </li>
          <li>
            <div
              onClick={() => {
                onProfileSidebarHide();
                mLogout.mutate(user);
              }}
              className="cursor-pointer flex surface-border mb-3 p-3 align-items-center border-1 surface-border border-round hover:surface-hover transition-colors transition-duration-150"
            >
              <span>
                <i className="pi pi-power-off text-xl text-primary"></i>
              </span>
              <div className="ml-3">
                <span className="mb-2 font-semibold">
                  {t("layout.appProfileSidebar.actions.logout.title")}
                </span>
                <p className="text-color-secondary m-0">
                  {t("layout.appProfileSidebar.actions.logout.description")}
                </p>
              </div>
            </div>
          </li>
        </ul>
      </div>
      <Dialog
        header={t("layout.appProfileSidebar.actions.changeTimezone.title")}
        visible={isChangingTimezone}
        onHide={() => {
          setIsChangingTimezone(false);
          setTimeZone(user.timeZone);
        }}
        footer={() => (
          <>
            <Button
              label={t("common.cancel")}
              icon="pi pi-times"
              outlined
              onClick={() => {
                setIsChangingTimezone(false);
                setTimeZone(user.timeZone);
              }}
            />
            <Button
              label={t("common.confirm")}
              icon="pi pi-check"
              severity="success"
              onClick={async () => {
                await mUpdateUser.mutateAsync({ ...user, timeZone });
                setIsChangingTimezone(false);
              }}
              disabled={mUpdateUser.isLoading}
            />
          </>
        )}
      >
        <Dropdown
          inputId="timeZone"
          name="timeZone"
          className="w-full"
          value={timeZone}
          options={timezoneOptions}
          optionLabel="label"
          optionValue="value"
          onChange={(e) => {
            setTimeZone(e.value);
          }}
          disabled={mUpdateUser.isLoading}
        />
      </Dialog>
    </Sidebar>
  );
};

export default AppProfileSidebar;
